import { useCallback, useEffect, useRef, useState } from "react"
import { Bot, Loader2, Paperclip, Send, User } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { MessageAttachments } from "@/components/conversations/message-attachments/MessageAttachments"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import { previewIaApi, type PreviewMessage } from "@/features/preview-ia/api/previewIaApi"
import type { AttachmentType, MessageAttachment } from "@/types/conversations"

function guessAttachmentKind(file: File): AttachmentType {
  const t = file.type || ""
  if (t.startsWith("video/")) return "video"
  if (t.startsWith("image/")) return "image"
  if (t.startsWith("audio/")) return "audio"
  return "document"
}

function buildOptimisticAttachments(
  files: File[],
  nonce: number
): { attachments: MessageAttachment[]; blobUrls: string[] } {
  const blobUrls: string[] = []
  const attachments = files.map((file, i) => {
    const url = URL.createObjectURL(file)
    blobUrls.push(url)
    const kind = guessAttachmentKind(file)
    return {
      id: `local-${nonce}-${i}`,
      type: kind,
      attachment_type: kind,
      mime_type: file.type || "application/octet-stream",
      filename: file.name,
      file_url: url,
      transcription: "",
      ai_description: "",
      caption: "",
      latitude: null,
      longitude: null,
      location_name: "",
      location_address: "",
    }
  })
  return { attachments, blobUrls }
}

const formatTime = (value?: string) => {
  const date = value ? new Date(value) : new Date()
  return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
}

const isRenderableBotMessage = (message: PreviewMessage) =>
  message.sender !== "user" && Boolean((message.text || "").trim())

const BACKGROUND_SYNC_INTERVAL_MS = 3000

const toMillis = (message: PreviewMessage): number => {
  const value = message.timestamp || message.created_at
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeMessages = (messages: PreviewMessage[]): PreviewMessage[] =>
  [...messages].sort((a, b) => {
    const diff = toMillis(a) - toMillis(b)
    if (diff !== 0) return diff
    return (a.id || "").localeCompare(b.id || "")
  })

const buildServerSnapshotSignature = (messages: PreviewMessage[]): string =>
  messages
    .map((message) => `${message.id}|${message.sender}|${message.timestamp || message.created_at || ""}|${message.text || ""}`)
    .join("||")

export function PreviewIAPage() {
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<PreviewMessage[]>([])
  const [input, setInput] = useState("")
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [isThinking, setIsThinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const optimisticBlobUrlsRef = useRef<string[]>([])
  const serverSnapshotSignatureRef = useRef("")

  const revokeOptimisticBlobs = () => {
    for (const url of optimisticBlobUrlsRef.current) {
      URL.revokeObjectURL(url)
    }
    optimisticBlobUrlsRef.current = []
  }

  const applyServerSnapshot = useCallback((incoming: PreviewMessage[]) => {
    const normalized = normalizeMessages(incoming)
    const signature = buildServerSnapshotSignature(normalized)
    if (signature === serverSnapshotSignatureRef.current) return
    serverSnapshotSignatureRef.current = signature
    setMessages(normalized)
  }, [])

  useEffect(() => {
    const boot = async () => {
      setLoading(true)
      setError(null)
      try {
        const user = await previewIaApi.createDemoUser()
        const conversation = await previewIaApi.createConversation(user.id)
        setConversationId(conversation.id)
        const initialMessages = await previewIaApi.listMessages(conversation.id)
        applyServerSnapshot(initialMessages)
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo inicializar Preview IA")
      } finally {
        setLoading(false)
      }
    }
    void boot()
  }, [applyServerSnapshot])

  useEffect(() => {
    if (!conversationId) return

    let cancelled = false

    const syncOnce = async () => {
      if (sending || cancelled) return
      try {
        const latest = await previewIaApi.listMessages(conversationId)
        if (cancelled) return
        applyServerSnapshot(latest)
      } catch {
        // Silent retry on next cycle.
      }
    }

    const intervalId = window.setInterval(() => {
      void syncOnce()
    }, BACKGROUND_SYNC_INTERVAL_MS)

    void syncOnce()

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [conversationId, sending, applyServerSnapshot])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [messages])

  useEffect(() => {
    const ref = optimisticBlobUrlsRef
    return () => {
      for (const url of ref.current) {
        URL.revokeObjectURL(url)
      }
      ref.current = []
    }
  }, [])

  const submit = async () => {
    if (!conversationId || sending) return
    const text = input.trim()
    if (!text && selectedFiles.length === 0) return
    const botMessagesBeforeSend = messages.filter(isRenderableBotMessage).length
    setInput("")
    const pendingFiles = [...selectedFiles]
    setSelectedFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ""
    setSending(true)
    setIsThinking(true)
    setError(null)
    const knownIdsBeforeSend = new Set(messages.map((message) => message.id))

    revokeOptimisticBlobs()
    const optimisticNonce = Date.now()
    const { attachments: optimisticAttachments, blobUrls } =
      pendingFiles.length > 0
        ? buildOptimisticAttachments(pendingFiles, optimisticNonce)
        : { attachments: [] as MessageAttachment[], blobUrls: [] as string[] }
    optimisticBlobUrlsRef.current = blobUrls

    const tempId = `tmp-${optimisticNonce}`
    const tempMessage: PreviewMessage = {
      id: tempId,
      sender: "user",
      text,
      created_at: new Date().toISOString(),
      attachments: optimisticAttachments,
    }
    setMessages((prev) => [...prev, tempMessage])

    try {
      const result = await previewIaApi.sendMessage(conversationId, text, pendingFiles)
      // First, reconcile immediately with the response payload.
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.id !== tempId)
        const next = [...withoutTemp]
        if (result.userMessage) next.push(result.userMessage)
        if (result.botMessage) next.push(result.botMessage)
        return next
      })
      queueMicrotask(() => revokeOptimisticBlobs())

      // For web turns the backend often responds asynchronously.
      // Poll messages list until we observe a new bot message count.
      if (!result.botMessage) {
        const knownIds = new Set(knownIdsBeforeSend)
        if (result.userMessage?.id) knownIds.add(result.userMessage.id)
        const maxAttempts = 40
        let latestSnapshot: PreviewMessage[] = []
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          const latest = await previewIaApi.listMessages(conversationId)
          latestSnapshot = latest
          const botMessagesNow = latest.filter(isRenderableBotMessage).length
          const hasNewBotMessage = latest.some(
            (message) => isRenderableBotMessage(message) && !knownIds.has(message.id)
          )
          if (hasNewBotMessage || botMessagesNow > botMessagesBeforeSend) {
            applyServerSnapshot(latest)
            setIsThinking(false)
            break
          }

          // Keep state aligned even when no new bot message has arrived yet.
          if (latest.length > 0) {
            applyServerSnapshot(latest)
          }
        }
        if (latestSnapshot.length > 0) {
          applyServerSnapshot(latestSnapshot)
        }
      } else {
        // Keep state fully synchronized even when bot responds inline.
        const latest = await previewIaApi.listMessages(conversationId)
        if (latest.length > 0) {
          applyServerSnapshot(latest)
        }
      }

      if (result.turnStatus === "failed") {
        setError(result.turnError || "No se pudo generar la respuesta del asistente")
      }
      if (result.botMessage) {
        setIsThinking(false)
      }
    } catch (e) {
      revokeOptimisticBlobs()
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      setError(e instanceof Error ? e.message : "No se pudo enviar el mensaje")
    } finally {
      setIsThinking(false)
      setSending(false)
    }
  }

  return (
    <div className="h-full p-4 md:p-6">
      <Card className="mx-auto flex h-full w-full max-w-5xl flex-col">
        <CardHeader className="border-b">
          <CardTitle className="text-base md:text-lg">Preview IA</CardTitle>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
                {messages.map((message) => {
                  const isUser = message.sender === "user"
                  return (
                    <div key={message.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`flex min-w-0 max-w-[85%] items-start gap-2 ${isUser ? "flex-row-reverse" : ""}`}
                      >
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback>{isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}</AvatarFallback>
                        </Avatar>
                        <div
                          className={`min-w-0 max-w-full overflow-hidden rounded-lg px-3 py-2 text-sm ${isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}
                        >
                          {isUser ? (
                            <>
                              {message.text ? (
                                <p className="whitespace-pre-wrap break-words">{message.text}</p>
                              ) : null}
                              <MessageAttachments
                                attachments={message.attachments || []}
                                attachmentContext="user"
                              />
                            </>
                          ) : (
                            <>
                              <div className="prose prose-sm max-w-none dark:prose-invert">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {message.text || ""}
                                </ReactMarkdown>
                              </div>
                              <MessageAttachments
                                attachments={message.attachments || []}
                                attachmentContext="assistant"
                              />
                            </>
                          )}
                          <p className="mt-1 text-xs opacity-70">{formatTime(message.timestamp || message.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {isThinking && (
                  <div className="flex justify-start">
                    <div className="flex min-w-0 max-w-[85%] items-start gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          <Bot className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
                        <div className="mb-1 text-xs text-muted-foreground">Thinking...</div>
                        <div className="flex items-center gap-1">
                          <span className="h-2 w-2 animate-pulse rounded-full bg-current [animation-delay:-0.3s]" />
                          <span className="h-2 w-2 animate-pulse rounded-full bg-current [animation-delay:-0.15s]" />
                          <span className="h-2 w-2 animate-pulse rounded-full bg-current" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {error && (
                <div className="px-4 pb-3 md:px-6">
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </div>
              )}

              <Separator />
              <div className="flex flex-col gap-2 p-4 md:p-6">
                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedFiles.map((file) => (
                      <span
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="inline-flex max-w-full items-center rounded-md border border-border/60 bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground"
                      >
                        <span className="min-w-0 max-w-full break-all">{file.name}</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-stretch gap-2">
                  <div className="flex min-h-10 flex-1 items-center gap-1 rounded-xl border border-input bg-background/80 px-1 shadow-sm ring-offset-background transition-[box-shadow] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 dark:bg-muted/20">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="sr-only"
                      tabIndex={-1}
                      onChange={(event) => {
                        const files = Array.from(event.target.files || [])
                        setSelectedFiles(files)
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Adjuntar archivos"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Input
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      placeholder="Escribe tu mensaje..."
                      className="min-w-0 flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm"
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault()
                          void submit()
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    onClick={() => void submit()}
                    disabled={(!input.trim() && selectedFiles.length === 0) || sending}
                    aria-label="Enviar mensaje"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
